/**
 * ReviewPromptModal — iter1114
 *
 * Light-touch modal that pops up on the dashboard when the backend
 * says the user is eligible for a review prompt (lab uploaded or
 * 7-day meal streak, account >= 14 days old, hasn't been prompted
 * in the last 30 days, hasn't already left a review).
 *
 * Interaction:
 *   • "Остави отзив" → scroll/navigate to FeedbackPanel with Review tab preselected
 *   • "По-късно" → snoozes for 30 days (POST /me/review-prompt-dismiss)
 *   • Dismiss (X) → session-only dismiss, will re-prompt next visit
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Star, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const TRIGGER_HEADLINE = {
  first_lab_upload:       "Качихте първото си лабораторно изследване 🎉",
  seven_day_meal_streak:  "7 дни последователен ритъм! 🔥",
};
const TRIGGER_SUBLINE = {
  first_lab_upload:
    "Как премина? Отделете 30 секунди и ни споделете какво работи и какво не.",
  seven_day_meal_streak:
    "Поздравления за постоянството! Споделете как Nutramilo Ви помага.",
};

export default function ReviewPromptModal() {
  const { API, user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [state, setState] = useState({ eligible: false, trigger: null });
  const [open, setOpen] = useState(false);
  const [sessionDismissed, setSessionDismissed] = useState(false);

  useEffect(() => {
    if (!user) return;
    // small delay so this doesn't block the dashboard first paint
    const t = setTimeout(() => {
      axios
        .get(`${API}/me/review-prompt-eligible`)
        .then(({ data }) => {
          if (data?.eligible) {
            setState(data);
            setOpen(true);
          }
        })
        .catch(() => {});
    }, 2500);
    return () => clearTimeout(t);
  }, [API, user]);

  if (!open || sessionDismissed || !state.eligible) return null;

  const dismissSession = () => setSessionDismissed(true);

  const snooze30d = async () => {
    try {
      await axios.post(`${API}/me/review-prompt-dismiss`);
    } catch {}
    setOpen(false);
  };

  const goToReview = () => {
    setOpen(false);
    // Scroll to the Feedback panel (Dashboard renders it) + flag for the panel to open.
    try {
      sessionStorage.setItem("nutramilo_open_review_tab", "1");
    } catch {}
    const el = document.querySelector('[data-testid="feedback-panel"]');
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      navigate("/dashboard?review=1");
    }
  };

  const headline = TRIGGER_HEADLINE[state.trigger] || "Как Ви харесва Nutramilo?";
  const subline  = TRIGGER_SUBLINE[state.trigger]  || "Оценете ни с ⭐ — отнема 30 секунди.";
  const cardBg   = isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200";

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="review-prompt-modal"
    >
      <div
        className={`${cardBg} relative rounded-2xl border-2 shadow-2xl w-full max-w-sm overflow-hidden`}
      >
        {/* Dismiss */}
        <button
          onClick={dismissSession}
          className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-slate-500/15"
          aria-label="Затвори"
          data-testid="review-prompt-close"
        >
          <X className="w-4 h-4 opacity-60" />
        </button>

        {/* Stars accent */}
        <div className="flex items-center justify-center gap-0.5 pt-5">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star key={s} className="w-5 h-5 text-amber-500 fill-amber-500" />
          ))}
        </div>

        <div className="px-5 pb-5 pt-2 text-center">
          <h3 className="font-bold text-base mb-1">{headline}</h3>
          <p className={`text-xs leading-snug ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            {subline}
          </p>

          <div className="mt-4 flex flex-col gap-2">
            <Button
              onClick={goToReview}
              className="h-9 text-xs"
              data-testid="review-prompt-cta"
            >
              Оставете отзив ⭐
            </Button>
            <button
              onClick={snooze30d}
              className={`text-[11px] opacity-70 hover:opacity-100 ${
                isDark ? "text-slate-400" : "text-slate-500"
              }`}
              data-testid="review-prompt-snooze"
            >
              По-късно (не ме питай 30 дни)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
